import { Injectable, NgZone } from "@angular/core";
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
  SimpleSnackBar,
} from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { NGXLogger } from "ngx-logger";
import { OverlayContainerService } from "app/core/services/overlay-container.service";

export enum SnackbarMode {
  Default,
  CinemaMode,
  CinemaModeWithSidebar,
}

@Injectable({
  providedIn: "root",
})
export class SnackBarService {
  private static overlayCinemaModeClass: string =
    "cdk-overlay-container--cinema-mode";
  private static overlayCinemaModeWithSideBarClass: string =
    "cdk-overlay-container--cinema-mode-with-sidebar";

  constructor(
    private zone: NgZone,
    private translateService: TranslateService,
    private matSnackBar: MatSnackBar,
    private logger: NGXLogger,
    private overlayContainerService: OverlayContainerService,
  ) {}

  /**
   * Show Snack Bar
   */
  public open(
    message: string,
    action?: string,
    config?: MatSnackBarConfig,
    messageParam?: object,
  ): MatSnackBarRef<SimpleSnackBar> {
    message = this.translateService.instant(message, messageParam);
    action = action && this.translateService.instant(action);
    this.logger.debug("Open Material snack-bar with config:", config);
    return this.matSnackBar.open(message, action, config);
  }

  public setMode(mode: SnackbarMode): void {
    this.overlayContainerService.removeClass(
      SnackBarService.overlayCinemaModeClass,
    );
    this.overlayContainerService.removeClass(
      SnackBarService.overlayCinemaModeWithSideBarClass,
    );

    switch (mode) {
      case SnackbarMode.CinemaMode: {
        this.overlayContainerService.addClass(
          SnackBarService.overlayCinemaModeClass,
        );
        break;
      }
      case SnackbarMode.CinemaModeWithSidebar: {
        this.overlayContainerService.addClass(
          SnackBarService.overlayCinemaModeWithSideBarClass,
        );
        break;
      }
      default: {
        // Nothing.
      }
    }
  }

  /**
   * Show snack bar with duration of 2 seconds, if not set
   */
  public openInfo(
    message: string,
    timeout?: boolean,
    action?: string,
    config?: MatSnackBarConfig,
    messageParam?: object,
  ): void {
    if (!config || !config.duration) {
      config = {
        ...config,
        duration: 2000,
      };
    }
    if (timeout) {
      setTimeout(() => this.open(message, action, config, messageParam), 2000);
    } else {
      // NgZone is used to fix NFON-9156
      // @see https://github.com/angular/components/issues/9875#issuecomment-375545786
      this.zone.run(() => {
        this.open(message, action, config, messageParam);
      });
    }
  }

  /**
   * Show snack bar with error, with duration of 5 seconds, if not set
   */
  public openErrorInfo(
    message: string,
    action?: string,
    config?: MatSnackBarConfig,
    messageParam?: object,
  ): void {
    if (!config || !config.duration) {
      config = {
        ...config,
        duration: 5000,
      };
    }
    this.open(message, action, config, messageParam);
  }

  /**
   * Dismisses the currently-visible snack bar
   */
  public dismiss(): void {
    this.matSnackBar.dismiss();
  }
}
